import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { ArrowLeft, ArrowRight, X } from "lucide-react";
import { toast } from "sonner";
import { ProfileWizardSteps, WizardStep } from "./ProfileWizardSteps";
import { BasicInfoStep } from "./steps/BasicInfoStep";
import { PlatformsAndPayloadsStep } from "./steps/PlatformsAndPayloadsStep";
import { AssignmentStep } from "./steps/AssignmentStep";
import { Platform, Profile } from "@/types/profile";
import { ProfileTemplate } from "./ProfileTemplateSelection";
import { selectProfileIcon } from "@/utils/profileIcons";
import { getPayloadById } from "@/utils/payloads";

const formSchema = z.object({
  name: z.string().min(1, { message: "Name is required" }),
  description: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

interface NewProfileWizardProps {
  template?: ProfileTemplate;
  onSave: (profile: Omit<Profile, "id" | "createdAt" | "updatedAt">, isDraft?: boolean) => Profile;
}

export const NewProfileWizard = ({ template, onSave }: NewProfileWizardProps) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<WizardStep>("general");
  const [completedSteps, setCompletedSteps] = useState<WizardStep[]>([]);

  // Form state
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      description: template?.description || "",
    },
  });

  // Wizard state
  const [selectedPlatforms, setSelectedPlatforms] = useState<Platform[]>([]);
  const [customers, setCustomers] = useState<string[]>([]);
  const [groups, setGroups] = useState<string[]>([]);
  const [tags, setTags] = useState<string[]>([]);
  const [selectedPayloads, setSelectedPayloads] = useState<string[]>([]);
  const [payloadConfigs, setPayloadConfigs] = useState<Record<string, Record<string, any>>>({});
  const [templatePayloads, setTemplatePayloads] = useState<string[]>([]);

  // Auto-configure based on template
  useEffect(() => {
    if (template) {
      // Map template platform strings to Platform enum
      const platformMap: Record<string, Platform> = {
        'macOS': 'macos',
        'iOS': 'ios',
        'iPadOS': 'ipados',
        'tvOS': 'tvos'
      };
      
      const mappedPlatforms = template.platforms
        .map(p => platformMap[p])
        .filter(Boolean) as Platform[];
      
      const templatePayloadIds = template.payloads || [];
      
      setSelectedPlatforms(mappedPlatforms);
      setSelectedPayloads(templatePayloadIds);
      setTemplatePayloads(templatePayloadIds);
      
      // Log for debugging
      console.log('Template configured:', {
        platforms: mappedPlatforms,
        payloads: templatePayloadIds
      });
    }
  }, [template]);

  const steps: WizardStep[] = ["general", "targeting", "review"];

  const canProceedFromStep = (step: WizardStep): boolean => {
    switch (step) {
      case "general":
        const { name } = form.getValues();
        return !!name && name.trim().length > 0;
      case "targeting":
        return selectedPlatforms.length > 0 && selectedPayloads.length > 0;
      case "review":
        return customers.length > 0;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (!canProceedFromStep(currentStep)) {
      form.trigger();
      return;
    }

    if (!completedSteps.includes(currentStep)) {
      setCompletedSteps(prev => [...prev, currentStep]);
    }

    const currentIndex = steps.indexOf(currentStep);
    if (currentIndex < steps.length - 1) {
      setCurrentStep(steps[currentIndex + 1]);
    }
  };

  const handleBack = () => {
    const currentIndex = steps.indexOf(currentStep);
    if (currentIndex > 0) {
      setCurrentStep(steps[currentIndex - 1]);
    }
  };

  const handleDeploy = () => {
    if (!form.formState.isValid) {
      form.trigger();
      return;
    }

    const formData = form.getValues();
    
    const payloadsData = selectedPayloads.map(payloadId => ({
      payloadId,
      configValues: payloadConfigs[payloadId] || {}
    }));

    // Get full payload objects for icon selection
    const payloadObjects = selectedPayloads
      .map(id => getPayloadById(id))
      .filter(Boolean) as any[];

    // Auto-select icon based on payloads (or use template icon if available)
    const autoSelectedIcon: string = (template?.icon as string) || selectProfileIcon(payloadObjects);

    const profileData: Omit<Profile, "id" | "createdAt" | "updatedAt"> = {
      name: formData.name,
      description: formData.description || "",
      platforms: selectedPlatforms,
      payloads: payloadsData,
      customer: customers.length > 0 ? customers[0] : undefined,
      customers: customers,
      groups: groups,
      tags: tags,
      icon: autoSelectedIcon
    };

    const savedProfile = onSave(profileData);
    
    toast.success("Profile deployed successfully");
    navigate(`/profiles/${savedProfile.id}`);
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case "general":
        return <BasicInfoStep form={form} />;
      case "targeting":
        return (
          <PlatformsAndPayloadsStep
            selectedPlatforms={selectedPlatforms}
            onPlatformChange={setSelectedPlatforms}
            selectedPayloads={selectedPayloads}
            payloadConfigs={payloadConfigs}
            onSelectPayload={setSelectedPayloads}
            onConfigurePayload={setPayloadConfigs}
            templatePayloads={templatePayloads}
          />
        );
      case "review":
        return (
          <AssignmentStep
            customers={customers}
            groups={groups}
            tags={tags}
            setCustomers={setCustomers}
            setGroups={setGroups}
            setTags={setTags}
            selectedPlatforms={selectedPlatforms}
            selectedPayloads={selectedPayloads}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="border-b bg-background">
        <div className="flex items-center justify-between h-16 px-6">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate("/profiles")}
            >
              <X className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-lg font-bold">Create Profile</h1>
              {template && (
                <p className="text-sm text-muted-foreground">
                  Based on {template.name} template
                </p>
              )}
            </div>
          </div>
          
          <div className="flex gap-2">
            {currentStep === "review" ? (
              <Button 
                onClick={handleDeploy}
                disabled={!canProceedFromStep(currentStep)}
              >
                Deploy Profile
              </Button>
            ) : (
              <>
                {currentStep !== "general" && (
                  <Button 
                    variant="outline"
                    onClick={handleBack}
                  >
                    <ArrowLeft className="h-4 w-4 mr-1" /> Back
                  </Button>
                )}
                <Button 
                  onClick={handleNext}
                  disabled={!canProceedFromStep(currentStep)}
                >
                  Next <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Step Indicator */}
      <ProfileWizardSteps 
        currentStep={currentStep}
        completedSteps={completedSteps}
      />

      {/* Content */}
      <div className="flex-1 overflow-auto">
        <div className="p-6">
          <Form {...form}>
            <form onSubmit={(e) => e.preventDefault()}>
              {renderStepContent()}
            </form>
          </Form>
        </div>
      </div>
    </div>
  );
};